import { useMemo } from 'react';

import useProfileSizes from '~/pdp/utils/useProfileSizes';
import { CustomerDetailNames } from '~/shared/constants/customerDetailNames';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import useGender from '~/shared/utils/useGender';
import useMembershipPrice from '~/shared/utils/useMembershipPrice';
import {
  useMembership,
  useLoyalty,
  useCustomerDetail,
} from '~/techstyle-shared/react-accounts';
import { useIntl } from '~/techstyle-shared/react-intl';
import { useMarketingDisposition } from '~/techstyle-shared/react-marketing';
import {
  useDomain,
  useSession,
  useSessionDetail,
} from '~/techstyle-shared/redux-core';

import { getBuilderMembershipStatus } from './getBuilderMembershipStatus';
import { getBuilderPageTargeting } from './getBuilderPageTargeting';

export default function useBuilderTargeting({ isPreview = false }) {
  // There may be some tradeoff efficiencies to preload the user attributes at the SSR level
  // to minimize re-renders on initial load. However, this should be done sparingly
  // as it can increase the time to first byte (TTFB) and slow down the initial page load.
  const { isLoggedIn } = useSession();
  const { locale } = useIntl();
  const { gender } = useGender();
  const membership = useMembership();
  const price = useMembershipPrice();
  const loyalty = useLoyalty();
  const membershipStatus = getBuilderMembershipStatus(membership, isLoggedIn);
  const vipPlusPerksAvailable = membership?.vipPlusPerksAvailable;
  const disposition = useMarketingDisposition();
  const experienceId = disposition?.experience;
  const { profileSizes } = useProfileSizes({ isPdp: false });
  const { tld } = useDomain();
  const isFirstBilling = Boolean(membership?.isFirstBilling);
  const developerDefinedTargetingId = useCustomerDetail(
    CustomerDetailNames.DEVELOPER_DEFINED_TARGETING_ID
  )?.value;
  const { value: sessionDeveloperDefinedTargetingId } = useSessionDetail(
    CustomerDetailNames.DEVELOPER_DEFINED_TARGETING_ID
  );

  // Prefer the customer detail value, but fall back to the session value
  const finalDeveloperDefinedTargetingId =
    developerDefinedTargetingId || sessionDeveloperDefinedTargetingId;

  const {
    size_bottom: bottomSize = null,
    size_top: topSize = null,
    size_bra: braSize = null,
    size_band: bandSize = null,
    size_cup: cupSize = null,
    size_bralette: braletteSize = null,
    size_lingerie: lingerieSize = null,
  } = profileSizes || {};

  const sizeFields = useMemo(() => {
    return getBrandValue({
      default: {
        // For Fabletics
        bottomSize,
        topSize,
        braSize,
        bandSize: null,
        cupSize: null,
        braletteSize: null,
        lingerieSize: null,
      },
      savage: {
        // For Savage X Fenty
        bottomSize,
        topSize: null,
        braSize: null,
        bandSize,
        cupSize,
        braletteSize,
        lingerieSize,
      },
    });
  }, [
    bottomSize,
    topSize,
    braSize,
    bandSize,
    cupSize,
    braletteSize,
    lingerieSize,
  ]);

  const pageOptions = useMemo(
    () => ({
      experienceId,
      gender,
      locale,
      membershipStatus,
      isPreview,
      loyalty,
      vipPlusPerksAvailable,
      ...sizeFields,
      membershipPrice: price?.toString() || '',
      tld,
      isFirstBilling,
      developerDefinedTargetingId: finalDeveloperDefinedTargetingId,
    }),
    [
      experienceId,
      gender,
      locale,
      membershipStatus,
      isPreview,
      loyalty,
      vipPlusPerksAvailable,
      sizeFields,
      price,
      tld,
      isFirstBilling,
      finalDeveloperDefinedTargetingId,
    ]
  );

  // Because objects are not referentially equal between re-renders,
  // this will always return a new object, so we need to memoize the return value
  const builderPageTargetingOptions = useMemo(
    () => getBuilderPageTargeting(pageOptions),
    [pageOptions]
  );

  return builderPageTargetingOptions;
}
